/**
 * Copyright 2022 (C) Blink - All Rights Reserved
 *
 * This source code is protected under international copyright law.  All rights
 * reserved and protected by the copyright holders.
 * All files are confidential and only available to authorized individuals with the
 * permission of the copyright holders. If you encounter any file and do not have
 * permission, please get in touch with the copyright holders and delete this file.
 */
import { useEffect, useRef, useState } from "react";

import cn from "classnames";

import { ChevronDown } from "components/icons";

import s from "./Collapsible.module.css";

const DEFAULT_INITIAL_MAX_HEIGHT = 10000;

const Collapsible = ({
  children,
  isOpenByDefault = false,
  shouldCalculateMaxHeight = true,
  ...args
}) => {
  const [isOpen, setIsOpen] = useState(isOpenByDefault);
  // if children is a function, this means he is going to use Collapsible.Header and Collapsible.Menu
  if (typeof children === "function") {
    return children(isOpen, setIsOpen);
  }

  return (
    <CollapsibleMenu
      shouldCalculateMaxHeight={shouldCalculateMaxHeight}
      isOpen={args.isOpen}
    >
      {children}
    </CollapsibleMenu>
  );
};

const CollapsibleMenu = ({
  children,
  initialMaxHeight = DEFAULT_INITIAL_MAX_HEIGHT,
  minHeight = 150,
  isOpen,
  className = "",
  shouldCalculateMaxHeight = true,
}) => {
  const collapseMenuRef = useRef(null);
  const isFirstRender = useRef(true);
  const maxHeightRef = useRef(initialMaxHeight);
  useEffect(() => {
    if (collapseMenuRef.current) {
      if (!isOpen) {
        collapseMenuRef.current.className += " overflow-y-hidden";
      }
    }

    if (
      collapseMenuRef.current &&
      !isFirstRender.current &&
      shouldCalculateMaxHeight
    ) {
      if (maxHeightRef.current < minHeight) {
        maxHeightRef.current = minHeight;
      }

      if (
        (maxHeightRef.current > collapseMenuRef.current.offsetHeight &&
          maxHeightRef.current !== initialMaxHeight) ||
        collapseMenuRef.current.offsetHeight < minHeight
      ) {
        // Someone collapsed the menu too early! The offsetHeight is not full.
        return;
      }

      maxHeightRef.current = collapseMenuRef.current.offsetHeight;
    }

    if (isOpen && isFirstRender.current) {
      isFirstRender.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => {
    let t;
    if (isOpen) {
      t = setTimeout(() => {
        if (collapseMenuRef.current) {
          if (isOpen) {
            // remove the overflow-y-hidden class
            collapseMenuRef.current.className =
              collapseMenuRef.current.className.replace(
                "overflow-y-hidden",
                "",
              );
          }
        }
      }, 300);
    }

    return () => t && clearTimeout(t);
  }, [isOpen]);

  return (
    <div
      className={cn(
        s.root,
        "overflow-y-hidden",
        isOpen ? "duration-500" : "duration-300",
        className,
      )}
      style={isOpen ? { maxHeight: maxHeightRef.current } : { maxHeight: 0 }}
      ref={collapseMenuRef}
    >
      {children}
    </div>
  );
};

const CollapsibleHeader = ({
  children,
  isOpen,
  onClick,
  arrowProps = {},
  className = "",
  hasArrow = true,
}) => {
  return (
    <div
      onClick={() => onClick(!isOpen)}
      className={cn("flex  items-center space-x-2", className)}
    >
      {children}

      {hasArrow && (
        <span
          className={cn(
            "w-4 h-4 flex justify-between items-center text-white",
            arrowProps?.className,
          )}
        >
          <ChevronDown
            className={cn(
              "duration-300 w-full h-full",
              isOpen ? "rotate-180" : "rotate-0",
            )}
          />
        </span>
      )}
    </div>
  );
};

Collapsible.Header = CollapsibleHeader;
Collapsible.Menu = CollapsibleMenu;

export default Collapsible;
